import React, { useEffect, useMemo } from 'react'
import { Box, Checkbox, ListItemText, SxProps, TextField, Theme, Tooltip } from '@mui/material'
import { useField } from 'formik'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import MenuItem from '@mui/material/MenuItem'
import HelperTooltip from './HelperTooltip'
import { useDebounce } from 'use-debounce'
import { DEFAULT_DEBOUNCE_TIME } from '../../utils/defaultDebounceTime'
import { isArray, isNil } from 'lodash-es'
import { SelectOptions } from './Options'
import { compactFormFieldSx } from './DisplayedItem'

export const SelectOptionLabel: React.FC<{ option: SelectOptions }> = ({ option }) => (
  <>
    {option.label}
    {option.secondaryLabel && (
      <>
        {' '}
        <Box component="span" sx={{ fontSize: '0.75em', color: 'text.secondary' }}>
          ({option.secondaryLabel})
        </Box>
      </>
    )}
  </>
)

export interface SelectFieldProps {
  name: string
  label: string
  withNone?: boolean
  noneTranslation?: string
  autoFocus?: boolean
  fullWidth?: boolean
  required?: boolean
  multiple?: boolean
  disabled?: boolean
  placeholder?: string
  sx?: SxProps<Theme>
  onMouseLeave?: () => void
  onOpen?: () => void
  onClose?: () => void
  onChange?: (value: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void
  tooltipText?: string
  variant?: 'filled' | 'outlined' | 'standard'
  defaultHelperText?: string
  renderValue?: (value: unknown) => React.ReactNode
  options?: SelectOptions[]
  compact?: boolean
}

const SelectField: React.FC<SelectFieldProps> = ({
  name,
  label,
  withNone = false,
  noneTranslation = 'commons.emptyField',
  children,
  multiple = false,
  sx,
  onMouseLeave,
  onOpen,
  onClose,
  tooltipText,
  variant = 'standard',
  defaultHelperText = '',
  renderValue,
  onChange,
  options,
  compact = false,
  ...props
}) => {
  const t = useAppTranslations()
  const [field, meta, { setValue: setFormikValue }] = useField({ name })
  const [shownValue, setShownValue] = React.useState(field.value)

  const [realValueDebounced] = useDebounce(shownValue, multiple ? DEFAULT_DEBOUNCE_TIME : 0)

  useEffect(() => {
    if (field.value !== shownValue) setShownValue(field.value)
  }, [field.value])

  useEffect(() => {
    if (field.value !== shownValue) setFormikValue(realValueDebounced)
  }, [realValueDebounced])

  const hasError = Boolean(meta.touched && meta.error)
  const helperText = (meta.error && hasError && t(meta.error)) || defaultHelperText

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
    setShownValue(event.target?.value)
    if (onChange !== undefined) onChange(event)
  }

  const updatedRenderValue = useMemo(() => {
    if (!isNil(renderValue)) return renderValue

    const defaultValue = props.placeholder ?? t(noneTranslation) ?? ''

    if (!isNil(options))
      if (multiple)
        return (selected: unknown): React.ReactNode => {
          if (isArray(selected)) {
            if (selected.length === 0) return defaultValue

            return (options ?? [])
              .filter((toDisplay) => selected.includes(toDisplay.value))
              .map((selectedToDisplay) => selectedToDisplay.label)
              .join(', ')
          }
        }
      else
        return (selected: unknown): React.ReactNode => {
          const hasEmptySelection = isNil(selected) || selected === ''

          const emptyValue: string = !hasEmptySelection ? (selected as string) : defaultValue
          const option = (options ?? []).find((item) => item.value === selected)

          return option ? option.label : emptyValue
        }

    return undefined
  }, [renderValue, options, multiple])

  return (
    <Box onMouseLeave={onMouseLeave}>
      <TextField
        id={name}
        {...field}
        {...props}
        value={multiple && isNil(shownValue) ? [] : shownValue}
        hiddenLabel={compact}
        label={
          <>
            {t(label)}
            {tooltipText && <HelperTooltip helperText={tooltipText} sx={{ fontSize: 15, marginLeft: '6.5px' }} />}
          </>
        }
        variant={variant}
        size="small"
        margin={compact ? 'none' : 'normal'}
        select
        fullWidth
        error={hasError}
        helperText={helperText}
        onChange={handleChange}
        sx={compact ? { ...compactFormFieldSx, ...sx } : sx}
        slotProps={{
          select: {
            multiple: multiple,
            displayEmpty: withNone || !!updatedRenderValue,
            renderValue: updatedRenderValue,
            onOpen: onOpen,
            onClose: onClose,
          },

          inputLabel: compact ? { sx: { display: 'none' } } : { shrink: true },
        }}
      >
        {withNone && (
          <MenuItem value="">
            <em>{t(noneTranslation)}</em>
          </MenuItem>
        )}

        {isNil(options) && children}

        {!isNil(options) &&
          options.map((option, index) => (
            <MenuItem value={option.value.toString()} key={index} disabled={option.disabled ?? false}>
              {multiple && <Checkbox checked={field.value && field.value.includes(option.value)} />}
              {option.tooltip ? (
                <Tooltip placement="right" title={option.tooltip}>
                  <Box component="span" sx={{ width: '100%' }}>
                    <ListItemText primary={<SelectOptionLabel option={option} />} />
                  </Box>
                </Tooltip>
              ) : (
                <ListItemText primary={<SelectOptionLabel option={option} />} />
              )}
            </MenuItem>
          ))}
      </TextField>
    </Box>
  )
}

export default SelectField
